import { supabaseClient } from 'supabase'
import { ImportBeatRow } from './listImportBeats'

export type BeatArea = {
  area_city_id: number | null
  area_municipality: string | null
}

/**
 * Under RLS a refused UPDATE is not an error: it matches 0 rows and returns
 * `[]`. Treat that as a failure so a write that did not happen is never shown
 * as a success.
 */
const requireUpdated = (data: unknown[] | null): ImportBeatRow[] => {
  if (!data || data.length === 0) {
    throw new Error('beat not updated')
  }
  return data as ImportBeatRow[]
}

/**
 * Pause or resume a beat. Gated by the RLS policy "Admins update import beats"
 * (0028) and the column-level UPDATE grant on `active` (0028). The payload is
 * `{ active }` only; `handle` is the match key, never a value written.
 *
 * Errors are thrown, never swallowed; 0 rows updated throws 'beat not updated'.
 */
export const setBeatActive = async (
  handle: string,
  active: boolean
): Promise<ImportBeatRow[]> => {
  const { data, error } = await supabaseClient
    .from('import_beats')
    .update({ active })
    .eq('handle', handle)
    .select()

  if (error) {
    throw error
  }
  return requireUpdated(data)
}

/**
 * Set a beat's area (a municipality or one town, exactly one of the pair).
 * Gated by the RLS policy "Admins update import beats" (0028) and the
 * column-level UPDATE grant on the area columns (0028).
 *
 * There is deliberately no function that writes `page_url` or `handle`:
 * re-pointing a beat would orphan its run history, and 0028 grants no UPDATE
 * on either (D-16).
 *
 * Errors are thrown raw (so `mapBeatError` can read them), never swallowed;
 * 0 rows updated throws 'beat not updated'.
 */
export const setBeatArea = async (
  handle: string,
  area: BeatArea
): Promise<ImportBeatRow[]> => {
  const { data, error } = await supabaseClient
    .from('import_beats')
    .update({
      area_city_id: area.area_city_id,
      area_municipality: area.area_municipality
    })
    .eq('handle', handle)
    .select()

  if (error) {
    throw error
  }
  return requireUpdated(data)
}
